import { useState, useEffect } from "react";
import {
  Grid,
  Icon,
  Divider,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
} from "@mui/material";

import ModalRoot from "containers/modal/ModalRoot";
import SuiBox from "components/SuiBox";
import ModalHeader from "containers/modal-header";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomButton from "containers/CustomButton";
import ImageUpload from "components/ChooseImage/image";
import RichTextEditor from "components/RichTextEditor";
import MultipleTags from "containers/Tags";
import NutritionModal from "components/NutritionValueModal";
import { convertTo24HourFormat } from "utils/common";
import Createable from "containers/Select";
import { InputOption } from "utils/common";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  EditRecipe,
  AddRecipe,
  recipeSelector,
  clearState,
} from "store/features/recipes";
import { userSelector } from "store/features/authSlice";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { UnitState } from "utils/common";
import { foodCategory } from "utils/common";

const nutritionOptions = [
  "Protein",
  "Fat",
  "Carbohydrate",
  "Sugar",
  "Magnesium",
  "Fibre",
  "Calcium",
  "Iron",
];

const getUnitOption = (unit) => {
  const value = typeof unit === "string" ? unit : unit?.value;
  return UnitState?.find((u) => u.value === value) ?? null;
};

export default function RecipeModal({
  open,
  onClose,
  mode,
  editHandler,
  viewHandler,
  selectedItem,
  setMode,
  chartId,
  index,
  setSelectedItem,
  defaultMealSchedules,
  customSchedules,
  onLocalSave,
}) {
  const dispatch = useDispatch();
  const { uid } = useSelector(userSelector);
  const {
    isFetching,
    message: message_,
    isError,
    isSuccess,
  } = useSelector(recipeSelector);

  const defaultformData = {
    name: "",
    description: "",
    preparation: "",
    howToTake: "",
    nutritionValue: "",
    ingredients: "",
    foodType: "",
    groceryList: "",
    calories: "",
    qty: "",
    unit: "",
    foodCategory: "",
    image: null,
    goesWell: [],
    baseNutritions: [],
  };

  const defaultErrorMessage = {
    name: false,
    description: false,
    preparation: false,
    howToTake: false,
    nutritionValue: false,
    ingredients: false,
    foodType: false,
    groceryList: false,
    calories: false,
    qty: false,
    unit: false,
    foodCategory: false,
    image: false,
  };

  const combinedMealSchedules = [
    ...(defaultMealSchedules || []),
    ...(customSchedules || []),
  ];

  const [formData, setformData] = useState(defaultformData);
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [loader, setLoader] = useState(false);
  const [message, setMessage] = useState(null);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [editCalorie, setEditCalorie] = useState(false);
  const [caloriesPerUnit, setCaloriesPerUnit] = useState(null);
  const [constQuantity, setConstQuantity] = useState(null);
  const [constantSelectedItem, setConstantSelectedItem] = useState(null);
  const [editConfirmModal, setEditConfirmModal] = useState(false);
  const [baseNutritions, setBaseNutritions] = useState([]);

  useEffect(() => {
    if (selectedItem) {
      const newformData = {
        ...selectedItem,
    
        // 🔴 CRITICAL FIXES
        ingredients: Array.isArray(selectedItem.ingredients)
          ? selectedItem.ingredients
          : selectedItem.ingredients
          ? [selectedItem.ingredients]
          : [],
    
        groceryList: Array.isArray(selectedItem.groceryList)
          ? selectedItem.groceryList
          : selectedItem.groceryList
          ? [selectedItem.groceryList]
          : [],
    
        foodType: Array.isArray(selectedItem.foodType)
          ? selectedItem.foodType
          : selectedItem.foodType
          ? [selectedItem.foodType]
          : [],
    
        toggleMode: selectedItem.toggleMode || "",
        time: convertTo24HourFormat(selectedItem?.time),
    
        name: selectedItem.name
          ? selectedItem.name
          : selectedItem.name?.label
          ? selectedItem.name
          : { label: selectedItem.name, value: selectedItem.name },
    
        foodCategory: sanitizeFoodCategory(selectedItem.foodCategory),
    
        unit: selectedItem.unit?.label
          ? selectedItem.unit
          : { label: selectedItem.unit, value: selectedItem.unit },
    
        goesWell: Array.isArray(selectedItem.goesWell)
          ? selectedItem.goesWell
          : [],
      };
    
      setformData(newformData);
    }

    if (selectedItem?.id && constantSelectedItem?.id !== selectedItem?.id) {
      setConstantSelectedItem(selectedItem);
      const quantity = parseFloat(selectedItem?.qty || 1); // Prevent divide by 0
      const calories = parseFloat(selectedItem?.calories || 0);

      if (quantity > 0) {
        setCaloriesPerUnit(calories / quantity);
      } else {
        setCaloriesPerUnit(0);
      }
      setConstQuantity(quantity);
      setEditCalorie(false);
      setDefaultOptions(selectedItem?.nutritions ?? []);
    }

    if (
      selectedItem?.nutritions?.length &&
      constantSelectedItem?.id !== selectedItem?.id
    ) {
      setBaseNutritions(selectedItem.nutritions);
      setSelectedOptions(selectedItem.nutritions); // <-- ADD THIS

      setDefaultOptions(selectedItem.nutritions);
    } else {
      setBaseNutritions([]);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (mode === "create") {
      setformData(defaultformData);
      setBaseNutritions([]);
      setSelectedOptions([]);
    }
  }, [mode]);

  useEffect(() => {
    if (isError) {
      toast.error(message_ || "Something went wrong!");
      dispatch(clearState());
    }

    if (isSuccess) {
      onClose();
      toast.success(message_ || "Recipe updated successfully!");
      dispatch(clearState());

      if (selectedItem) {
        setEditConfirmModal(false);
      }
    }
  }, [isError, isSuccess, message_, dispatch, selectedItem]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setMessage(null);
  };

  const calculateCalories = (qty) => {
    if (qty <= 0) return 0;
    if (!caloriesPerUnit) {
      const baseCalories = parseFloat(formData.calories || 0);
      const baseQty = parseFloat(formData.qty || 1);
      if (baseQty > 0) {
        return (baseCalories / baseQty) * qty;
      }
      return 0;
    }
    return parseFloat((caloriesPerUnit * qty).toFixed(2));
  };

  const handleFoodCategoryChange = (selected) => {
    setformData((prev) => ({
      ...prev,
      foodCategory: selected ?? [],
    }));
  };

  const handleAddNutrition = ({ grams, unit }) => {
    const existingIndex = selectedOptions.findIndex(
      (item) => item.type === selectedType
    );
    const newItem = { type: selectedType, grams, unit: unit || "g" };

    if (existingIndex !== -1) {
      const updatedSelected = [...selectedOptions];
      updatedSelected[existingIndex] = newItem;
      setSelectedOptions(updatedSelected);

      const updatedBase = [...baseNutritions];
      updatedBase[existingIndex] = newItem;
      setBaseNutritions(updatedBase);

      setformData((prev) => ({ ...prev, nutritions: updatedSelected }));
    } else {
      const updatedSelected = [...selectedOptions, newItem];
      const updatedBase = [...baseNutritions, newItem];

      setSelectedOptions(updatedSelected);
      setBaseNutritions(updatedBase);
      setformData((prev) => ({ ...prev, nutritions: updatedSelected }));
    }
    setModalOpen(false);
  };

  const calculateNutritionValue = (qty) => {
    if (!qty || isNaN(qty) || qty <= 0) return baseNutritions;

    return baseNutritions.map((item) => {
      let grams = parseFloat(item.grams);
      if (isNaN(grams)) grams = 0;

      let unit = item.unit || "g";

      let safeConstQuantity = parseFloat(constQuantity);
      if (!safeConstQuantity || safeConstQuantity <= 0) safeConstQuantity = 1; // Prevent divide by 0

      // Normalize to grams if unit is mg
      if (unit === "mg") {
        grams = grams / 1000; // convert mg to grams
      }

      let scaledGrams = grams * (qty / safeConstQuantity);

      // Convert back to original unit
      if (unit === "mg") {
        scaledGrams = scaledGrams * 1000; // convert back to mg
      }

      // Prevent NaN or Infinity
      if (!isFinite(scaledGrams)) scaledGrams = 0;

      return {
        ...item,
        grams: parseFloat(scaledGrams.toFixed(2)),
      };
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setformData((prev) => ({ ...prev, image: file }));
    }
  };

  const handleDeleteImage = () => {
    setformData({ ...formData, image: null });
  };

  const handleRemoveNutrition = (type) => {
    const updatedOptions = selectedOptions.filter((item) => item.type !== type);
    setSelectedOptions(updatedOptions);
    setDefaultOptions(defaultOptions.filter((item) => item.type !== type));
    setformData((prevData) => ({ ...prevData, nutritions: updatedOptions }));
  };

  const onChangeHandler = (e, isToggleClicked) => {
    const name = e?.target.name;
    const value = e?.target.value;
    if (isToggleClicked) {
      const toggleMode = e.target.checked ? "OR" : "AND";
      setformData((ee) => {
        ee["toggleMode"] = toggleMode;
        return ee;
      });
    } else {
      setformData((ee) => ({
        ...ee,
        [name]: value,
      }));
    }

    if (e.target.name === "calories" && selectedItem?.id) {
      setEditCalorie(true);
      setMessage(
        "You have changed the calories, please change qty and nutrition value manually."
      );
    }
    if (e.target.name === "unit" && selectedItem?.id) {
      setEditCalorie(true);
      setMessage(
        "You have changed the unit, please change calories, qty and nutrition value manually."
      );
    }

    if (e.target.name !== "calories" && e.target.name !== "unit") {
      setMessage(null);
    }

    if (name === "qty" && !editCalorie && selectedItem?.id) {
      const quantity = parseFloat(value);
      if (!isNaN(quantity) && quantity > 0) {
        const updatedCalories = calculateCalories(quantity);
        const updatedNutritionValue = calculateNutritionValue(quantity);

        setSelectedOptions(updatedNutritionValue);

        setformData((prevData) => ({
          ...prevData,
          calories: parseFloat(updatedCalories.toFixed(2)),
        }));
      }
    }
  };

  const sanitizeFoodCategory = (data) => {
    if (
      data &&
      typeof data === "object" &&
      Array.isArray(data.label) &&
      Array.isArray(data.value)
    ) {
      return data.label.map((label, idx) => ({
        label,
        value: data.value[idx] ?? label,
      }));
    }
    if (
      data &&
      typeof data === "object" &&
      typeof data.label === "string" &&
      typeof data.value === "string"
    ) {
      return [{ label: data.label, value: data.value }];
    }
    if (Array.isArray(data)) {
      return data
        .map((cat) =>
          typeof cat === "string"
            ? { label: cat, value: cat }
            : typeof cat === "object" && cat?.value
            ? cat
            : null
        )
        .filter(Boolean);
    }
    if (typeof data === "string") {
      return [{ label: data, value: data }];
    }
    return [];
  };

  const handlerAddRecipe = (e) => {
    e.preventDefault();

    if (onLocalSave && selectedItem) {
      onLocalSave({
        ...formData,
        foodCategory: (formData.foodCategory || []).map((c) =>
          typeof c === "object" ? c.value : c
        ),
        unit: formData.unit,
      });
      onClose();
      return;
    }

    if (!formData.name) {
      toast.error("Please enter a Recipe Name");
      setErrorMessage({
        ...errorMessage,
        name: true,
      });
      return;
    }

    if (!formData.foodCategory || formData.foodCategory.length === 0) {
      toast.error("Please fill Food Category field");
      setErrorMessage({
        ...errorMessage,
        foodCategory: true,
      });
      return;
    }

    if (formData.qty === "" || formData.qty === null) {
      toast.error("Please fill Quantity field");
      setErrorMessage({
        ...errorMessage,
        qty: true,
      });
      return;
    }

    if (!formData.unit) {
      toast.error("Please fill Unit field");
      setErrorMessage({
        ...errorMessage,
        unit: true,
      });
      return;
    }

    if (!formData.goesWell || formData.goesWell.length === 0) {
      toast.error("Please select at least one 'Goes Well With' schedule");
      setErrorMessage({
        ...errorMessage,
        goesWell: true,
      });
      return;
    }

    const sanitizedformData = {
      ...formData,
      foodCategory: formData.foodCategory.map((cat) => cat.value),
      unit: formData.unit,
    };

    if (selectedItem) {
      return setEditConfirmModal(true);
    }

    dispatch(AddRecipe({ formdata: sanitizedformData, uid }));
    setformData(defaultformData);
  };

  const editRecipeConfirmHandler = () => {
    const sanitizedformData = {
      ...formData,
      foodCategory: formData.foodCategory.map((cat) => cat.value),
      unit: formData.unit,
    };
    dispatch(EditRecipe({ formdata: sanitizedformData, uid }));
  };

  return (
    <>
      <ModalRoot
        variant="permanent"
        ownerState={{ openConfigurator: open }}
        width="78%"
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "#fff",
          },
        }}
      >
        <SuiBox
          style={{ whiteSpace: "normal !important" }}
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          pt={3}
          pb={0.8}
          px={3}
        >
          <ModalHeader mode={mode} title="recipe" />

          <Grid item>
            {mode === "view" && (
              <Icon
                sx={({
                  typography: { size, fontWeightBold },
                  palette: { dark },
                }) => ({
                  fontSize: `${size.md} !important`,
                  fontWeight: `${fontWeightBold} !important`,
                  stroke: dark.main,
                  strokeWidth: "2px",
                  cursor: "pointer",
                  mt: 2,
                  mr: 2,
                })}
                onClick={() => {
                  editHandler(selectedItem, "payment");
                }}
              >
                edit
              </Icon>
            )}

            <Icon
              sx={({
                typography: { size, fontWeightBold },
                palette: { dark },
              }) => ({
                fontSize: `${size.md} !important`,
                fontWeight: `${fontWeightBold} !important`,
                stroke: dark.main,
                strokeWidth: "2px",
                cursor: "pointer",
                mt: 2,
              })}
              onClick={() => {
                onClose();
                setformData(defaultformData);
                setSelectedOptions([]);
                setSelectedItem(null);
              }}
            >
              close
            </Icon>
          </Grid>
        </SuiBox>

        <Divider />

        <SuiBox
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          pt={3}
          pb={0.8}
          px={3}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Recipe Name*
                </SuiTypography>
                <SuiInput
                  fullWidth
                  type="text"
                  name="name"
                  value={formData.name ?? ""}
                  onChange={(e) =>
                    setformData((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }))
                  }
                  required
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Calories
                </SuiTypography>
                <SuiInput
                  type="number"
                  name="calories"
                  placeholder="--/--"
                  value={formData.calories}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value < 0) {
                      return;
                    }
                    onChangeHandler(e);
                    setformData((prev) => ({
                      ...prev,
                      calories: parseFloat(value),
                    }));
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Nutrition Value
                </SuiTypography>
                <Autocomplete
                  multiple
                  clearIcon={null}
                  id="nutrition-input"
                  sx={{
                    display: "flex",
                    borderBottom: "none",
                    "& input": {
                      width: 200,
                      bgcolor: "background.paper",
                      borderBottom: "none",
                    },
                  }}
                  options={nutritionOptions}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.type
                  }
                  value={
                    Array.isArray(selectedOptions)
                      ? selectedOptions.map((option) => option.type)
                      : []
                  }
                  onChange={(event, values) => {
                    const latest = values[values.length - 1];
                    if (!selectedOptions.find((item) => item.type === latest)) {
                      setModalOpen(true);
                      setSelectedType(latest);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Nutrition"
                      autoFocus
                      sx={{
                        "& .MuiAutocomplete-inputRoot": {
                          overflowX: "auto",
                          display: "flex !important",
                          alignItems: "flex-start !important",
                          flexWrap: "wrap !important",
                          minHeight: "80px !important",
                          height: "auto !important",

                          "&::after": {
                            borderBottom: "none !important",
                          },
                          "&::before": {
                            borderBottom: "none !important",
                          },
                          "&:hover": {
                            background: "red",
                          },
                        },
                      }}
                      variant="filled"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const item = selectedOptions.find(
                        (x) => x.type === option
                      );
                      return (
                        <Chip
                          key={option}
                          label={
                            item
                              ? `${option}: ${item.grams}${item.unit || "g"}`
                              : option
                          }
                          {...getTagProps({ index })}
                          onDelete={() => {
                            handleRemoveNutrition(option);
                          }}
                        />
                      );
                    })
                  }
                />
                <NutritionModal
                  open={modalOpen}
                  selectedType={selectedType}
                  onClose={handleCloseModal}
                  onAddNutrition={handleAddNutrition}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Ingredients
                </SuiTypography>
                <MultipleTags
                  placeholder="Ingredients"
                  valList={formData["ingredients"] || []}
                  disabled={mode === "view"}
                  height="80px !important"
                  freeTextHanlder={(val) => {
                    setformData((prev) => ({
                      ...prev,
                      ingredients: val,
                    }));
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Food Type
                </SuiTypography>
                <MultipleTags
                  placeholder="Food type"
                  valList={formData["foodType"] || []}
                  disabled={mode === "view"}
                  height="80px !important"
                  freeTextHanlder={(val) => {
                    setformData((prev) => ({
                      ...prev,
                      foodType: val,
                    }));
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Grocery List
                </SuiTypography>
                <MultipleTags
                  placeholder="Grocery items"
                  valList={formData["groceryList"] || []}
                  disabled={mode === "view"}
                  height="80px !important"
                  freeTextHanlder={(val) => {
                    setformData((prev) => ({
                      ...prev,
                      groceryList: val,
                    }));
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Quantity*
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="number"
                  name="qty"
                  readOnly={mode === "view"}
                  value={formData.qty}
                  onChange={(e) => {
                    let numberRegex = /^[0-9][0-9]{0,5}?(\.[0-9]{1,2})?$/;
                    let val = e.target.value;
                    let match_ = val.match(numberRegex);
                    if (!match_ && val) {
                      return false;
                    }
                    onChangeHandler(e);
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Unit*
                </SuiTypography>
                <Createable
                  options={UnitState ?? []}
                  disabled={mode === "view"}
                  value={getUnitOption(formData.unit)}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.800rem",
                    borderRadius: "0.5rem",
                  }}
                  placeholder="Choose unit"
                  onChange={(e) => {
                    let eObj = { target: { value: e.value, name: "unit" } };
                    onChangeHandler(eObj);
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Food Category*
                </SuiTypography>
                <Createable
                  options={foodCategory ?? []}
                  disabled={mode === "view"}
                  value={formData.foodCategory}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.800rem",
                    borderRadius: "0.5rem",
                  }}
                  placeholder="Choose food category"
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={{
                    Option: InputOption,
                  }}
                  onChange={handleFoodCategoryChange}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Recipe Description
                </SuiTypography>
                <RichTextEditor
                  value={formData.description || ""}
                  name="description"
                  onChange={(e) =>
                    setformData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  toolbar={true}
                  readOnly={mode === "view"}
                  error={errorMessage?.description}
                  placeholder="Enter recipe description..."
                  borderRadius="10px"
                  onlyText={false}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  How to Take
                </SuiTypography>
                <RichTextEditor
                  value={formData.howToTake || ""}
                  name="howToTake"
                  onChange={(e) =>
                    setformData((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }))
                  }
                  toolbar={true}
                  readOnly={mode === "view"}
                  error={errorMessage?.howToTake}
                  placeholder="Explain how to take this recipe..."
                  borderRadius="10px"
                  onlyText={false}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} md={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  How to Prepare
                </SuiTypography>
                <RichTextEditor
                  value={formData.preparation || ""}
                  name="preparation"
                  onChange={(e) =>
                    setformData((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }))
                  }
                  toolbar={true}
                  readOnly={mode === "view"}
                  error={errorMessage?.preparation}
                  placeholder="Explain how to prepare this recipe..."
                  borderRadius="10px"
                  onlyText={false}
                />
              </SuiBox>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <SuiBox lineHeight={1}>
              <SuiTypography mb={1} variant="h6">
                Goes Well With
              </SuiTypography>
              <Grid container spacing={2}>
                {combinedMealSchedules.map((option) => (
                  <Grid item key={option.id}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Checkbox
                          checked={formData?.goesWell?.includes(option.id)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setformData((prev) => ({
                              ...prev,
                              goesWell: isChecked
                                ? [...prev.goesWell, option.id]
                                : prev.goesWell.filter((v) => v !== option.id),
                            }));
                          }}
                          color="primary"
                        />
                      </Grid>
                      <Grid item>
                        {option.icon ? (
                          option.icon.startsWith("http") ? (
                            <img
                              src={option.icon}
                              alt={option.title}
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                objectFit: "cover",
                                marginRight: 6,
                              }}
                            />
                          ) : (
                            <span style={{ marginRight: 6 }}>
                              {option.icon}
                            </span>
                          )
                        ) : null}
                        <SuiTypography
                          variant="body2"
                          display="inline"
                          sx={{
                            verticalAlign: "middle",
                            textTransform: "capitalize",
                          }}
                        >
                          {option.title}
                        </SuiTypography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </SuiBox>
          </Grid>

          <ImageUpload
            id="recipe-image-upload"
            variant="dashed"
            image={formData.image}
            onChange={handleFileChange}
            onDelete={handleDeleteImage}
            label="Upload Recipe Image"
            avatarLabel="Upload ⬆"
            previewHeight={120}
            width={250}
          />

          <Grid container spacing={2} mb={5}>
            <Grid item xs={6}>
              <CustomButton
                color="dark"
                variant="outlined"
                fullWidth
                onClick={() => setformData(defaultformData)}
                disabled={loader || selectedItem}
                title="Clear Recipe"
              />
            </Grid>
            <Grid item xs={6}>
              <CustomButton
                color="dark"
                variant="gradient"
                fullWidth
                onClick={handlerAddRecipe}
                disabled={loader}
                loader={isFetching}
                title={selectedItem ? "Edit Recipe" : "Add Recipe"}
              />
            </Grid>
          </Grid>
        </SuiBox>
      </ModalRoot>
      {editConfirmModal && (
        <ConfirmationModal
          isFetching={isFetching}
          title="Are you sure you want to edit original recipe?"
          onCancelHandler={() => setEditConfirmModal(false)}
          onConfirmHandler={editRecipeConfirmHandler}
        />
      )}
    </>
  );
}
